import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Link, useLocation} from "react-router";
import clsx from "clsx";
import MenuIcon from "./menu-icon.jsx";
import MenuBadge from "./menu-badge.jsx";
import {findMenuItem, resolveTo} from "../utils/menu-utils.js";
import projectUtils from "../../../utils/project-utils.js";
import homeService from "../../../services/web/home-service.js";

const isActive = (node, activeItem) => {
    if (node === activeItem) return true;
    return node.items?.some((child) => isActive(child, activeItem)) ?? false;
};

const positionFlyout = (el, triggerTop) => {
    if (!el) return;
    const height = el.scrollHeight || 0;
    const maxTop = window.innerHeight - height - 8;
    el.style.top = `${Math.max(8, Math.min(triggerTop, maxTop))}px`;
};

const MenuLeaf = ({node, level, onNavigate, badge}) => {
    const to = resolveTo(node.url);
    if (!to) return null;

    const content = (
        <>
            {level === 1 && <MenuIcon icon={node.icon} image={node.image} />}
            <span className="mdb-menu-label">{node.name}</span>
            <MenuBadge count={badge} showCount />
        </>
    );

    if (node.target === "_blank") {
        const isVmsCard = node.id?.startsWith("card-vms");

        const openVms = () => {
            const vmsTab = window.open("", "_blank");
            if (!vmsTab) {
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS. Verifique se os pop-ups estão habilitados.", "Ok");
                return;
            }

            vmsTab.opener = null;
            homeService.accessVms(node.url)
                .then((url) => {
                    vmsTab.location.href = url;
                })
                .catch(() => {
                    vmsTab.close();
                    projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
                })
        }

        return (
            <a
                className="mdb-menu-link"
                href={isVmsCard ? undefined : node.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                    if (isVmsCard) openVms();
                    onNavigate();
                }}
            >
                {content}
            </a>
        );
    }

    return (
        <Link className="mdb-menu-link" to={to} onClick={onNavigate}>
            {content}
        </Link>
    );
};

const Level3 = ({node, activeItem, onNavigate, badges}) => {
    if (node.horizontalRule) {
        return (
            <li className="mdb-menu-item">
                <hr className="mdb-menu-divider" />
            </li>
        );
    }

    return (
        <li className={clsx("mdb-menu-item", isActive(node, activeItem) && "active")} id={`menu-${node.id}`}>
            <MenuLeaf node={node} level={3} onNavigate={onNavigate} badge={badges[node.id]} />
        </li>
    );
};

const Level2 = ({node, activeItem, open2, onToggle2, sub3Ref, onNavigate, badges}) => {
    if (node.horizontalRule) {
        return (
            <li className="mdb-menu-item">
                <hr className="mdb-menu-divider" />
            </li>
        );
    }

    const branch = node.items?.length > 0;
    const liClass = clsx("mdb-menu-item", isActive(node, activeItem) && "active");

    if (!branch) {
        return (
            <li className={liClass} id={`menu-${node.id}`}>
                <MenuLeaf node={node} level={2} onNavigate={onNavigate} badge={badges[node.id]} />
            </li>
        );
    }

    const isOpen = open2 === node.id;

    return (
        <li className={liClass} id={`menu-${node.id}`}>
            <a
                className="mdb-menu-link mdb-menu-toggle"
                role="button"
                href={`#sub-${node.id}`}
                aria-expanded={isOpen}
                onClick={(e) => {
                    e.preventDefault();
                    onToggle2(node.id, e.currentTarget.getBoundingClientRect().top);
                }}
            >
                <span className="mdb-menu-label">{node.name}</span>
                <MenuBadge count={badges[node.id]} />
                <i className="mdb-menu-arrow fa fa-chevron-right" />
            </a>

            <ul
                className={clsx("mdb-submenu mdb-submenu-3 list-unstyled", isOpen && "show")}
                id={`sub-${node.id}`}
                ref={isOpen ? sub3Ref : null}
            >
                {node.items.map((child) => (
                    <Level3 key={child.id || child.name} node={child} activeItem={activeItem} onNavigate={onNavigate} badges={badges} />
                ))}
            </ul>
        </li>
    );
};

const Level1 = ({node, activeItem, open1, onToggle1, sub2Ref, open2, onToggle2, sub3Ref, onNavigate, badges}) => {
    const branch = node.items?.length > 0;
    const liClass = clsx("mdb-menu-item", isActive(node, activeItem) && "active");

    if (!branch) {
        return (
            <li className={liClass} id={`menu-${node.id}`}>
                <MenuLeaf node={node} level={1} onNavigate={onNavigate} badge={badges[node.id]} />
            </li>
        );
    }

    const isOpen = open1 === node.id;

    return (
        <li className={liClass} id={`menu-${node.id}`}>
            <a
                className="mdb-menu-link mdb-menu-toggle"
                role="button"
                href={`#sub-${node.id}`}
                aria-expanded={isOpen}
                onClick={(e) => {
                    e.preventDefault();
                    onToggle1(node.id, e.currentTarget.getBoundingClientRect().top);
                }}
            >
                <MenuIcon icon={node.icon} image={node.image} />
                <span className="mdb-menu-label">{node.name}</span>
                <MenuBadge count={badges[node.id]} />
                <i className="mdb-menu-arrow fa fa-chevron-right" />
            </a>

            <ul
                className={clsx("mdb-submenu mdb-submenu-2 list-unstyled", isOpen && "show")}
                id={`sub-${node.id}`}
                ref={isOpen ? sub2Ref : null}
            >
                {node.items.map((child) => (
                    <Level2
                        key={child.id || child.name}
                        node={child}
                        activeItem={activeItem}
                        open2={open2}
                        onToggle2={onToggle2}
                        sub3Ref={sub3Ref}
                        onNavigate={onNavigate}
                        badges={badges}
                    />
                ))}
            </ul>
        </li>
    );
};

const SidebarMenu = ({menu, badges = {}}) => {
    const {pathname} = useLocation();
    const activeItem = findMenuItem(menu, pathname);
    const [open1, setOpen1] = useState(null);
    const [open2, setOpen2] = useState(null);
    const sub2Ref = useRef(null);
    const sub3Ref = useRef(null);
    const top1 = useRef(0);
    const top2 = useRef(0);

    const closeAll = () => {
        setOpen1(null);
        setOpen2(null);
    };

    const toggle1 = (id, triggerTop) => {
        top1.current = triggerTop;
        setOpen2(null);
        setOpen1((prev) => (prev === id ? null : id));
    };

    const toggle2 = (id, triggerTop) => {
        top2.current = triggerTop;
        setOpen2((prev) => (prev === id ? null : id));
    };

    useLayoutEffect(() => {
        if (open1) positionFlyout(sub2Ref.current, top1.current);
    }, [open1]);

    useLayoutEffect(() => {
        if (open2) positionFlyout(sub3Ref.current, top2.current);
    }, [open2]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (e.target.closest(".mdb-submenu") || e.target.closest(".mdb-menu-toggle")) return;
            closeAll();
        };
        const onResize = () => {
            if (open1) positionFlyout(sub2Ref.current, top1.current);
            if (open2) positionFlyout(sub3Ref.current, top2.current);
        };
        document.addEventListener("click", onDocClick);
        window.addEventListener("resize", onResize);
        return () => {
            document.removeEventListener("click", onDocClick);
            window.removeEventListener("resize", onResize);
        };
    }, [open1, open2]);

    return (
        <ul className="mdb-menu-root list-unstyled" id="mdbMenuRoot">
            {menu.map((node) => (
                <Level1
                    key={node.id || node.name}
                    node={node}
                    activeItem={activeItem}
                    open1={open1}
                    onToggle1={toggle1}
                    sub2Ref={sub2Ref}
                    open2={open2}
                    onToggle2={toggle2}
                    sub3Ref={sub3Ref}
                    onNavigate={closeAll}
                    badges={badges}
                />
            ))}
        </ul>
    );
};

export default SidebarMenu;
